import React, {useCallback, useEffect, useState} from "react";
import {C} from "../styles";
import {useToast, useConfirm} from "./FeedbackProvider";
import {
    apiAdminPublishLanding, apiAdminDiscardLandingDraft, apiAdminListLandingVersions, apiAdminRestoreLandingVersion,
} from "../api/landing";

const SOURCE_LABELS = {initial: "Version d'origine", publish: "Publication", restore: "Retour arrière"};

const button = {border: `1px solid ${C.border}`, background: "#fff", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 14};

function errorText(result) {
    const first = Array.isArray(result.errors) && result.errors[0];
    return first ? `${result.message || result.error} (${first.path} : ${first.message})` : (result.message || result.error);
}

const formatDate = (iso) => new Date(iso).toLocaleString("fr-FR", {day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"});

// beforePublish (facultatif) : appelé après la confirmation et avant l'envoi ; false = ne pas publier
// (sert à enregistrer d'abord le dernier changement du brouillon).
export default function LandingPublishBar({isDraft, liveVersionId, onPublished, onDiscarded, beforePublish}) {
    const {notify} = useToast();
    const confirm = useConfirm();
    const [note, setNote] = useState("");
    const [versions, setVersions] = useState([]);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const loadVersions = useCallback(async () => {
        const result = await apiAdminListLandingVersions();
        setVersions(Array.isArray(result) ? result : []);
    }, []);

    useEffect(() => { loadVersions(); }, [loadVersions, liveVersionId]);

    async function publish() {
        setError("");
        if (!(await confirm("Publier cette version de la landing ? Elle sera visible par tous les visiteurs en moins d'une minute."))) return;
        setBusy(true);
        try {
            if (beforePublish && !(await beforePublish())) return;
            let result = await apiAdminPublishLanding({note});
            if (result && result.error === "STALE_DRAFT") {
                const force = await confirm(
                    "La page en ligne a changé depuis l'ouverture de votre brouillon (modifiée par quelqu'un d'autre). Publier quand même ? Ses changements seront remplacés par les vôtres (ils restent récupérables dans l'historique).",
                    {danger: true});
                if (!force) return;
                result = await apiAdminPublishLanding({note, force: true});
            }
            if (!result || result.error) {
                setError(result ? errorText(result) : "Impossible de publier.");
                return;
            }
            setNote("");
            notify("Landing publiée.", "success");
            onPublished();
        } finally {
            setBusy(false);
        }
    }

    async function discard() {
        setError("");
        if (!(await confirm("Abandonner le brouillon ? Les modifications non publiées seront perdues.", {danger: true}))) return;
        const result = await apiAdminDiscardLandingDraft();
        if (!result || result.error) {
            setError(result ? errorText(result) : "Impossible d'abandonner le brouillon.");
            return;
        }
        notify("Brouillon abandonné.", "success");
        onDiscarded();
    }

    async function restore(id) {
        setError("");
        const draftWarning = isDraft ? " Votre brouillon en cours sera abandonné (restaurer le remplace)." : "";
        if (!(await confirm(`Revenir à la version ${id} ? Elle sera publiée à la place de la version actuelle (celle-ci reste dans l'historique).${draftWarning}`, {danger: true}))) return;
        const result = await apiAdminRestoreLandingVersion(id);
        if (!result || result.error) {
            setError(result ? errorText(result) : "Impossible de restaurer cette version.");
            return;
        }
        notify(`Version ${id} remise en ligne.`, "success");
        onPublished();
    }

    return (
        <div style={{marginTop: 16}}>
            <div style={{display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 8}}>
                <label style={{flex: "1 1 220px", fontSize: 13}}>
                    Note (facultatif)
                    <input value={note} maxLength={200} onChange={e => setNote(e.target.value)} disabled={!isDraft}
                           style={{display: "block", width: "100%", boxSizing: "border-box", padding: "7px 9px", border: `1px solid ${C.border}`, borderRadius: 6}}/>
                </label>
                <button type="button" onClick={publish} disabled={!isDraft || busy}
                        style={{...button, background: isDraft ? C.primary : "#ccc", color: "#fff", border: "none", fontWeight: 600}}>Publier</button>
                <button type="button" onClick={discard} disabled={!isDraft || busy} style={button}>Abandonner le brouillon</button>
            </div>
            {error && <p role="alert" style={{color: C.danger, margin: "0 0 8px"}}>{error}</p>}

            <h4 style={{margin: "14px 0 6px"}}>Versions publiées</h4>
            <ul style={{listStyle: "none", margin: 0, padding: 0}}>
                {versions.map(v => (
                    <li key={v.id} data-testid={`version-${v.id}`}
                        style={{display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderBottom: `1px solid ${C.border}`, padding: "6px 0", fontSize: 13}}>
                        <span style={{flex: "1 1 260px"}}>
                            <strong>#{v.id}</strong> — {formatDate(v.published_at)} — {v.published_by_username || "Système"} — {SOURCE_LABELS[v.source] || v.source}
                            {v.note ? ` — ${v.note}` : ""}
                        </span>
                        {v.id === liveVersionId && <span style={{background: C.primaryLight, borderRadius: 10, padding: "2px 8px", fontSize: 12}}>En ligne</span>}
                        <button type="button" style={button} aria-label={`Revenir à la version ${v.id}`} disabled={v.id === liveVersionId}
                                onClick={() => restore(v.id)}>Revenir à cette version</button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
